import styled from "@emotion/styled";
import BaseButton from "src/atoms/Button/BaseButton";
import { hideScrollbar } from "src/scrollbar";

const StyledProductPhotos = styled.div<{ isInModal: boolean }>`
  width: 100%;

  :hover {
    cursor: ${({ isInModal }) => (isInModal ? "inherit" : "pointer")};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: ${({ isInModal }) => (isInModal ? "100%" : "416px")};
    margin-left: ${({ theme, isInModal }) =>
      isInModal ? "0" : theme.space[4]}px;
  }

  .swiper-pagination-bullet-active {
    background: ${({ theme }) => theme.colors.tone.black};
  }
`;

const StyledButtonNext = styled(BaseButton)`
  position: absolute;
  z-index: 2;
  cursor: pointer;
  border: none;
  background: none;
  right: ${({ theme }) => theme.space[2]}px;
  top: 50%;
  transform: rotate(270deg);

  &.swiper-button-disabled {
    display: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }

  svg {
    width: 40px;
    fill: ${({ theme }) => theme.colors.tone.anthraciteLight};
  }
`;

const StyledButtonBack = styled(StyledButtonNext)`
  right: unset;
  left: ${({ theme }) => theme.space[2]}px;
  transform: rotate(90deg);
`;

const StyledDotsPagination = styled.div`
  display: flex;
  justify-content: center;
  span {
    background-color: ${({ theme }) => theme.colors.tone.black};
    margin: ${({ theme }) => theme.space[3]}px
      ${({ theme }) => theme.space[2]}px;
    cursor: pointer;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const PhotosContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  column-gap: ${({ theme }) => theme.space.space16};
  grid-row: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100vw;
    aspect-ratio: 2 / 3;
    margin-inline: -${({ theme }) => theme.space.space16};
    grid-template-columns: 1fr;
    grid-row: span 2;
  }

  .selected-image {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    height: 100%;
    cursor: pointer;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      display: none;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .labels-wrapper {
    position: absolute;
    left: ${({ theme }) => theme.space.space8};
    bottom: ${({ theme }) => theme.space.space8};

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      left: ${({ theme }) => theme.space.space16};
      bottom: ${({ theme }) => theme.space.space32};
    }
  }

  .zoomed-image-container {
    display: none;
  }
`;

const PhotoArrowButton = styled.button`
  position: absolute;
  width: 100%;
  max-width: 80px;
  height: fit-content;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 9;

  &.hidden {
    display: none;
  }

  &:first-of-type {
    top: -20px;
    left: 0;

    svg {
      transform: rotate(180deg);
    }
  }

  &:last-of-type {
    bottom: -20px;
    left: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const PhotoModalContainer = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 100dvh;
  }

  .zoomed-image-container {
    position: absolute;
    top: 0;
    left: 0;
    display: none;
    width: auto;
    z-index: 10;
    cursor: zoom-out;

    &.visible {
      display: block;
    }

    img {
      width: 100%;
      height: auto;
    }

    .carousel-item {
      @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        pointer-events: none;
      }
    }
  }
`;

const PhotoModalCloseWrapper = styled.div`
  width: ${({ theme }) => theme.space.space48};
  aspect-ratio: 1 / 1;
  position: absolute;
  top: ${({ theme }) => theme.space.space16};
  right: ${({ theme }) => theme.space.space16};
  z-index: 110;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    right: unset;
    left: ${({ theme }) => theme.space.space16};

    button {
      border: none;
      opacity: 1;
    }
  }
`;

const ImageCarouselContainer = styled.div<{
  numberOfPhotos: number;
  selectorPosition: number;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 1920px;
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }

  .image-carousel-wrapper {
    display: grid;
    grid-template-columns: repeat(
      ${({ numberOfPhotos }) => numberOfPhotos},
      100%
    );
    height: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    ${hideScrollbar}

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      max-width: 100vw;
    }

    &.no-scroll {
      overflow-x: hidden;
      scroll-snap-type: none;

      .carousel-item {
        scroll-snap-stop: none;
        scroll-snap-align: none;
      }
    }

    .carousel-item {
      position: relative;
      width: 100%;
      scroll-snap-stop: always;
      scroll-snap-align: center;
      background: none;
      border: none;
      cursor: zoom-in;
      align-self: center;
      justify-self: center;

      &.zoomed {
        width: fit-content;
      }

      .image-wrapper {
        position: relative;
        width: 100%;
        height: 100%;

        &.zoomed {
          position: fixed;
          top: 0;
          left: 0;
          height: 100%;
          aspect-ratio: 2 / 3;
          overflow: auto;
          z-index: 100;
          cursor: zoom-out;
          ${hideScrollbar}

          img {
            aspect-ratio: 2 / 3;
            height: auto !important;
          }

          @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
            width: auto;
            height: 100%;
            pointer-events: none;

            img {
              width: auto;
              height: 100%;
              object-fit: cover;
            }
          }
        }
      }

      img {
        position: absolute;
        width: 100%;
        height: 100%;
        object-fit: contain;

        @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
          position: relative;
        }
      }

      .mobile-zoom-wrapper {
        width: 100vw;
        height: 100dvh;
      }

      .mobile-zoom-content {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          aspect-ratio: 2 / 3;
          height: auto !important;
        }
      }
    }
  }

  &.zoom {
    display: block;
    width: fit-content;

    .image-carousel-wrapper {
      aspect-ratio: 2 / 3;
    }

    .carousel-item {
      height: 100%;
      padding-block-start: ${({ theme }) => theme.space.space32};
      padding-block-end: ${({ theme }) => theme.space.space48};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      max-width: 100vw;

      .carousel-item {
        height: 100%;
        padding-block-start: 0;
        padding-block-end: 0;
      }
    }
  }

  .bullets-container {
    position: absolute;
    bottom: ${({ theme }) => theme.space.space32};
    left: 0;
    display: flex;
    width: 100%;
    height: ${({ theme }) => theme.space.space8};
    justify-content: center;
    place-items: center;
    z-index: 100;
    padding-inline: ${({ theme }) => theme.space.space16};

    &.zoom {
      padding-inline: ${({ theme }) => theme.space.space96};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      bottom: ${({ theme }) => theme.space.space16};

      &.zoom {
        padding-inline: ${({ theme }) => theme.space.space16};
      }
    }
  }

  .bullets-wrapper {
    position: relative;
    display: grid;
    grid-template-columns: repeat(
      ${({ numberOfPhotos }) => numberOfPhotos},
      1fr
    );
    width: 100%;
    height: ${({ theme }) => theme.space.space4};
    z-index: 100;

    gap: ${({ theme }) => theme.space.space4};

    &:before {
      content: "";
      position: absolute;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${({ theme }) => theme.colors.tone.black};
      transition: all 350ms ease-in-out;
      grid-column: 1 / 1;
      transform: translateX(${({ selectorPosition }) => selectorPosition}px);
    }

    &.is-first-position {
      &:before {
        border-top-left-radius: ${({ theme }) => theme.space.space4};
        border-bottom-left-radius: ${({ theme }) => theme.space.space4};
      }
    }

    &.is-last-position {
      &:before {
        border-top-right-radius: ${({ theme }) => theme.space.space4};
        border-bottom-right-radius: ${({ theme }) => theme.space.space4};
      }
    }

    .bullet {
      width: 100%;
      height: 100%;
      border: none;
      background-color: ${({ theme }) => theme.colors.tone.black};
      opacity: 0.2;

      &:first-of-type {
        border-top-left-radius: ${({ theme }) => theme.space.space4};
        border-bottom-left-radius: ${({ theme }) => theme.space.space4};
      }

      &:last-of-type {
        border-top-right-radius: ${({ theme }) => theme.space.space4};
        border-bottom-right-radius: ${({ theme }) => theme.space.space4};
      }

      &.selected {
        opacity: 1;
      }
    }
  }
`;

const ImageCarouselArrowButton = styled.button`
  position: absolute;
  top: 50%;
  width: ${({ theme }) => theme.space.space48};
  height: ${({ theme }) => theme.space.space48};
  display: flex;
  place-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.tone.silver};
  cursor: pointer;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }

  &.hidden {
    display: none;
  }

  &:first-of-type {
    left: ${({ theme }) =>
      `calc((${theme.space.space32 || "32px"} + ${
        theme.space.space16 || "16px"
      }) * -1)`};
  }

  &:last-of-type {
    right: ${({ theme }) =>
      `calc((${theme.space.space32 || "32px"} + ${
        theme.space.space16 || "16px"
      }) * -1)`};
    transform: rotate(180deg);
  }
`;

export {
  StyledProductPhotos,
  StyledButtonBack,
  StyledButtonNext,
  StyledDotsPagination,
  PhotosContainer,
  PhotoArrowButton,
  PhotoModalContainer,
  PhotoModalCloseWrapper,
  ImageCarouselContainer,
  ImageCarouselArrowButton,
};
